/**
 * Grading scales used across Bangladesh and international systems.
 * Sources: Bangladesh Education Board (SSC/HSC), University of Dhaka,
 * BUET, National University, Bangladesh Bureau of Educational
 * Information & Statistics (BANBEIS).
 */

export type ScaleId =
  | "ssc_hsc"
  | "bd_university_4"
  | "bd_private_4_pm"
  | "us_4_standard"
  | "us_4_pm"
  | "custom";

export interface GradeRow {
  letter: string;
  point: number;
  /** percentage range (inclusive). null when not applicable. */
  pct?: [number, number];
}

export interface GradingScale {
  id: ScaleId;
  name: { en: string; bn: string };
  source: { en: string; bn: string };
  max: number;
  grades: GradeRow[];
}

export const GRADING_SCALES: GradingScale[] = [
  {
    id: "ssc_hsc",
    name: { en: "SSC / HSC (Bangladesh) — 5.00", bn: "এসএসসি / এইচএসসি (৫.০০)" },
    source: { en: "Education Board, Bangladesh", bn: "শিক্ষা বোর্ড, বাংলাদেশ" },
    max: 5.0,
    grades: [
      { letter: "A+", point: 5.0, pct: [80, 100] },
      { letter: "A", point: 4.0, pct: [70, 79] },
      { letter: "A-", point: 3.5, pct: [60, 69] },
      { letter: "B", point: 3.0, pct: [50, 59] },
      { letter: "C", point: 2.0, pct: [40, 49] },
      { letter: "D", point: 1.0, pct: [33, 39] },
      { letter: "F", point: 0.0, pct: [0, 32] },
    ],
  },
  {
    id: "bd_university_4",
    name: { en: "University 4.00 (DU, BUET, NU)", bn: "বিশ্ববিদ্যালয় ৪.০০ (ঢাবি, বুয়েট, জাতীয়)" },
    source: { en: "University of Dhaka, BUET, National University", bn: "ঢাকা বিশ্ববিদ্যালয়, বুয়েট, জাতীয় বিশ্ববিদ্যালয়" },
    max: 4.0,
    grades: [
      { letter: "A+", point: 4.0, pct: [80, 100] },
      { letter: "A", point: 3.75, pct: [75, 79] },
      { letter: "A-", point: 3.5, pct: [70, 74] },
      { letter: "B+", point: 3.25, pct: [65, 69] },
      { letter: "B", point: 3.0, pct: [60, 64] },
      { letter: "B-", point: 2.75, pct: [55, 59] },
      { letter: "C+", point: 2.5, pct: [50, 54] },
      { letter: "C", point: 2.25, pct: [45, 49] },
      { letter: "D", point: 2.0, pct: [40, 44] },
      { letter: "F", point: 0.0, pct: [0, 39] },
    ],
  },
  {
    id: "bd_private_4_pm",
    name: { en: "Private University 4.00 (NSU, BRACU, IUB)", bn: "বেসরকারি বিশ্ববিদ্যালয় ৪.০০" },
    source: { en: "NSU, BRAC University, IUB", bn: "এনএসইউ, ব্র্যাক বিশ্ববিদ্যালয়, আইইউবি" },
    max: 4.0,
    grades: [
      { letter: "A", point: 4.0, pct: [93, 100] },
      { letter: "A-", point: 3.7, pct: [90, 92] },
      { letter: "B+", point: 3.3, pct: [87, 89] },
      { letter: "B", point: 3.0, pct: [83, 86] },
      { letter: "B-", point: 2.7, pct: [80, 82] },
      { letter: "C+", point: 2.3, pct: [77, 79] },
      { letter: "C", point: 2.0, pct: [73, 76] },
      { letter: "C-", point: 1.7, pct: [70, 72] },
      { letter: "D+", point: 1.3, pct: [67, 69] },
      { letter: "D", point: 1.0, pct: [60, 66] },
      { letter: "F", point: 0.0, pct: [0, 59] },
    ],
  },
  {
    id: "us_4_standard",
    name: { en: "US 4.00 Standard", bn: "ইউএস ৪.০০ (স্ট্যান্ডার্ড)" },
    source: { en: "United States, standard", bn: "যুক্তরাষ্ট্র, স্ট্যান্ডার্ড" },
    max: 4.0,
    grades: [
      { letter: "A", point: 4.0, pct: [90, 100] },
      { letter: "B", point: 3.0, pct: [80, 89] },
      { letter: "C", point: 2.0, pct: [70, 79] },
      { letter: "D", point: 1.0, pct: [60, 69] },
      { letter: "F", point: 0.0, pct: [0, 59] },
    ],
  },
  {
    id: "us_4_pm",
    name: { en: "US 4.00 with +/-", bn: "ইউএস ৪.০০ (+/-)" },
    source: { en: "United States, plus/minus", bn: "যুক্তরাষ্ট্র, প্লাস/মাইনাস" },
    max: 4.0,
    grades: [
      { letter: "A+", point: 4.0, pct: [97, 100] },
      { letter: "A", point: 4.0, pct: [93, 96] },
      { letter: "A-", point: 3.7, pct: [90, 92] },
      { letter: "B+", point: 3.3, pct: [87, 89] },
      { letter: "B", point: 3.0, pct: [83, 86] },
      { letter: "B-", point: 2.7, pct: [80, 82] },
      { letter: "C+", point: 2.3, pct: [77, 79] },
      { letter: "C", point: 2.0, pct: [73, 76] },
      { letter: "C-", point: 1.7, pct: [70, 72] },
      { letter: "D+", point: 1.3, pct: [67, 69] },
      { letter: "D", point: 1.0, pct: [63, 66] },
      { letter: "D-", point: 0.7, pct: [60, 62] },
      { letter: "F", point: 0.0, pct: [0, 59] },
    ],
  },
  {
    id: "custom",
    name: { en: "Custom Scale", bn: "কাস্টম স্কেল" },
    source: { en: "User defined", bn: "ব্যবহারকারী নির্ধারিত" },
    max: 4.0,
    grades: [
      { letter: "A", point: 4.0 },
      { letter: "B", point: 3.0 },
      { letter: "C", point: 2.0 },
      { letter: "D", point: 1.0 },
      { letter: "F", point: 0.0 },
    ],
  },
];

export function getScale(id: ScaleId): GradingScale {
  return GRADING_SCALES.find((s) => s.id === id) ?? GRADING_SCALES[0];
}

/** Map a numeric GPA back to a letter band (closest grade with point ≤ value). */
export function pointToLetter(scale: GradingScale, value: number): string {
  const sorted = [...scale.grades].sort((a, b) => b.point - a.point);
  for (const g of sorted) {
    if (value + 1e-9 >= g.point) return g.letter;
  }
  return sorted[sorted.length - 1].letter;
}
